package presentation.screens.history

import com.kinoshazam.Navigator
import com.kinoshazam.model.Route
import features.watch.WatchProgressInteractor
import features.watch.WatchRules
import features.watch.model.WatchEntry
import utils.BaseViewModel
import utils.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class HistoryViewModel(
    private val navigator: Navigator,
    private val watchProgressInteractor: WatchProgressInteractor,
) : BaseViewModel<HistoryState, HistoryMVIAction>() {

    private var all: List<WatchEntry> = emptyList()

    override fun initState(): HistoryState = HistoryState()

    init {
        launch {
            watchProgressInteractor.observe().collect { rows ->
                val entries = rows
                    .filter(WatchRules::inHistory)
                    .sortedByDescending { it.changedAt }
                all = entries
                publish()
            }
        }
    }

    override fun onAction(action: HistoryMVIAction) {
        when (action) {
            HistoryMVIAction.Back -> navigator.back()
            is HistoryMVIAction.Open -> open(action.entry)
            is HistoryMVIAction.Delete -> launch { watchProgressInteractor.delete(action.entry) }
        }
    }

    private fun open(entry: WatchEntry) {
        val url = entry.embedUrl()
        if (url == null) {
            navigator.navigateTo(Route.Dialog.Details(entry.videoIdInt, entry.isTvShow))
            return
        }
        navigator.navigateTo(
            Route.Dialog.WebPlayer(
                url = url,
                title = entry.title.orEmpty(),
                videoId = entry.videoIdInt,
                isTvShow = entry.isTvShow,
                seasonNumber = entry.seasonNumber,
                episodeNumber = entry.episodeNumber,
                imdbId = entry.imdbId.orEmpty(),
                imageUrl = entry.imageUrl.orEmpty(),
                backdropUrl = entry.backdropUrl.orEmpty(),
            )
        )
    }

    private fun publish() {
        val today = Clock.System.now().epochSeconds / 86_400
        val groups = HistoryBucket.entries.mapNotNull { bucket ->
            val items = all.filter { bucketOf(it.changedAt, today) == bucket }
            if (items.isEmpty()) null else HistoryGroup(bucket, items)
        }
        updateState { copy(groups = groups) }
    }

    private fun bucketOf(changedAt: String, today: Long): HistoryBucket {
        val day = runCatching { kotlin.time.Instant.parse(changedAt).epochSeconds / 86_400 }.getOrDefault(today)
        return when {
            day >= today -> HistoryBucket.TODAY
            day == today - 1 -> HistoryBucket.YESTERDAY
            day >= today - 6 -> HistoryBucket.WEEK
            else -> HistoryBucket.EARLIER
        }
    }
}
